import * as React from 'react';
import { ListItem, CircularProgress, List, Collapse } from '@material-ui/core';
import Scrollbars from 'react-custom-scrollbars';
import { SchedulerData } from '@markinson/mk.mpv4.schedulercomponent';
import * as styles from './LookUpList.scss';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { actions as activitySchedulerActions } from 'ducks/serviceActivityScheduling/serviceActivities';
import { isNull } from 'utils/utils';
import ListItemObj, { ListItemInner } from './ListItem';
import { inlineStyles } from './LookUpList.styles';
import { CIRCULAR_PROGRESS_SIZE, SCROLL_MARGIN } from './LookUpList.constants';
import { ILookUpListProperties, ILookUpListState } from './LookUpList.properties';

const DraggableListItem = (props) => {

  const {
    index, className, style, itemSelectedStyle, uniqueKey, item, dragAndDropSource, schedulerData, selectedIndex,
    idProp, beginDrag, endDrag, newEvent, listItemOnClick, isDraggable, disableItemSelection, fieldsToDisplay
  } = props;

  const DragAndDropSource = dragAndDropSource ? dragAndDropSource.getDragSource() : null;
  const isSelectedAndNotDraggable = (selectedIndex === index) && !dragAndDropSource;

  return (
    <ListItem
      button
      disabled={disableItemSelection}
      key={item?.TemplateId || uniqueKey}
      style={isSelectedAndNotDraggable ? { ...style, ...itemSelectedStyle } : { ...style, cursor: isDraggable ? 'move' : 'pointer' }}
      onClick={(e) => listItemOnClick(e, index, item[idProp])}
      selected={isSelectedAndNotDraggable}
      classes={{ root: className }}>
      {DragAndDropSource && isDraggable ?
        <DragAndDropSource
          key={item?.TemplateId || uniqueKey}
          beginDrag={beginDrag}
          endDrag={endDrag}
          item={item}
          schedulerData={schedulerData}
          newEvent={newEvent} >
          <ListItemInner {...props} />
        </DragAndDropSource> : Array.isArray(fieldsToDisplay) ? <ListItemInner {...props} /> : <ListItemObj {...props} />}
    </ListItem >
  );
};

class LookUpList<T extends object, U extends object> extends React.Component<ILookUpListProperties<T, U>, ILookUpListState> {

  scrollBars: React.RefObject<Scrollbars>;

  constructor(props: Readonly<ILookUpListProperties<T, U>>) {
    super(props);
    this.scrollBars = React.createRef<Scrollbars>();

    this.state = {
      cascadingOpen: props.cascadingListFields && { [1]: true },
      selectedIndex: props.disableDefaultSelection ? null : 0
    };
  }

  componentDidMount(): void {
    window.addEventListener('resize', this.resize);
    this.forceUpdate();
    this.setLastSelectedRecord();
  }

  componentWillUnmount(): void {
    window.removeEventListener('resize', this.resize);
  }

  componentDidUpdate(prevProps: Readonly<ILookUpListProperties<T, U>>): void {
    const { nextPage, loadMore, loadingPrevPage, loadingNextPage, fetchNextPage, selectedRecordId } = this.props;
    const values = this.scrollBars.current.getValues();
    if (nextPage && ((values.clientHeight) === values.scrollHeight && loadMore) && (!loadingPrevPage && !loadingNextPage)) {
      fetchNextPage();
    }
    if (values.scrollTop === 0) {
      this.scrollBars.current.scrollTop(1);
    }
    if (values.clientHeight + values.scrollTop === values.scrollHeight) {
      this.scrollBars.current.scrollTop(values.scrollTop - 1);
    }
    if (selectedRecordId && prevProps.selectedRecordId !== selectedRecordId) {
      this.handleSelectedRecordIdChange();
    }
  }

  handleSelectedRecordIdChange = () => {
    const { selectedRecordId, keyField, listItems } = this.props;
    const { selectedIndex } = this.state;

    if (!isNull(selectedRecordId)) {
      const selectedRecordItemIndex = listItems.findIndex((item) => item[keyField] === selectedRecordId);

      if (selectedRecordItemIndex !== selectedIndex) {
        this.handleListItemSelectionChange(null, selectedRecordItemIndex, selectedRecordId);
      }
    }

  }

  setLastSelectedRecord = () => {
    const { selectedRecordId = '', listItems = [], keyField, disableDefaultSelection } = this.props;
    const lastSelectedRecordIndex = !isNull(selectedRecordId) ?
      listItems.findIndex((item) => item[keyField] === selectedRecordId) : disableDefaultSelection ? null : 0;

    this.setState({ selectedIndex: lastSelectedRecordIndex });
  }
  resize = () => {
    this.forceUpdate();
  }

  handleListItemSelectionChange = (_event: React.ChangeEvent, idx: number, id: any) => {
    const { handleRecordChange, isNextItemSelectionAllowed, listItems } = this.props;

    if (isNextItemSelectionAllowed && !isNextItemSelectionAllowed(listItems[idx])) {
      return;
    }

    this.setState({ selectedIndex: idx });
    if (handleRecordChange) {
      handleRecordChange(id);
    }
  };

  isOverflown = () => {
    const element = this.scrollBars;
    if (element.current) {
      return element.current.getScrollHeight() > element.current.getClientHeight();
    }

    return false;
  }

  handleScroll = () => {
    const { loadingPrevPage, loadingNextPage, fetchPrevPage, fetchNextPage } = this.props;
    if (!loadingPrevPage && !loadingNextPage) {
      const { clientHeight, scrollTop, scrollHeight } = this.scrollBars.current.getValues();
      if ((clientHeight + scrollTop + SCROLL_MARGIN) >= scrollHeight && fetchNextPage) {
        fetchNextPage();
      } else if (scrollTop === 0 && fetchPrevPage) {
        fetchPrevPage();
      }
    }
  }

  toggleCollapse = (index: number): void => {
    if (this.state.cascadingOpen) {
      this.setState({ cascadingOpen: { ...this.state.cascadingOpen, [index]: !this.state.cascadingOpen[index] } });
    }
  }

  beginDrag = (props: any) => {
    const { schedulerData, triggerSchedulerDataChange } = this.props;

    if (props.item && props.item.ActivityStatus === 'C') return; //item is already completed

    const draggedActivityCapabilities = (props.item && props.item.Capabilities) ? props.item.Capabilities : [];
    const matchedRows = schedulerData.resources.filter((r) => draggedActivityCapabilities.every((c) => r.Capabilities.includes(c)));
    const unmatchedRows = schedulerData.resources.filter((value) => !matchedRows.includes(value));

    const frozenRowsData = [];

    schedulerData.resources.forEach((r) => {
      if (unmatchedRows.some((row) => row.id === r.id)) {
        frozenRowsData.push({
          slotId: r.id,
          freeze: true,
          freezeMessage: 'Capability requirement not met.'
        });
      }
    });

    schedulerData.setFrozenRows(frozenRowsData);
    triggerSchedulerDataChange();
  }

  endDrag = (_: any, monitor: any) => {
    const { schedulerData } = this.props;

    if (monitor.getDropResult) {
      const dropResult = monitor.getDropResult();
      if (dropResult && schedulerData.resources.some((d) => d.id === dropResult.slotId)) return;
    }
    this.unfreezeResources();
  }

  unfreezeResources = () => {
    const { schedulerData, triggerSchedulerDataChange } = this.props;
    schedulerData.setFrozenRows([]);
    triggerSchedulerDataChange();
  }

  newEvent = (schedulerData: SchedulerData, slotId: any, slotName: any, start: any, end: any, type: any, item: any) => {
    const { newSchedulerEvent } = this.props;
    const droppedSlot = schedulerData.renderData.find((d) => d.slotId === slotId);
    const firstFrozenHeaderItem = droppedSlot.headerItems.find((col) => col.start === start);
    const frozen = droppedSlot.freeze ? true : firstFrozenHeaderItem && firstFrozenHeaderItem.freeze;
    if (!frozen) {
      newSchedulerEvent(schedulerData, slotId, slotName, start, end, type, item);
    }
    this.unfreezeResources();
  }

  renderListChildList = (props: { children: U[]; index: number }): React.ReactElement<any> => {
    const { children, index } = props;
    const { browseLookUp, cascadingListKeyField, cascadingListFields, classes, isItemDraggable } = this.props;
    const open = this.state.cascadingOpen[index + 1];

    return (
      <Collapse in={open}
        timeout='auto'
        key={`nestedList-collapse-${index}.${index + 1}`}>
        <List
          component='nav'
          style={{
            padding: 0
          }}
          className={styles.lookupListingList}
          onChange={() => this.handleListItemSelectionChange}
          key={`nestedList-${index}`}
        >
          {children && children.map((item, i) => {
            return (
              <DraggableListItem
                {...this.props}
                {...this.state}
                key={i}
                index={i}
                isDraggable={isItemDraggable && isItemDraggable(item)}
                uniqueKey={`nested-${index}-${i + 1}`}
                item={item}
                className={`${classes.lookupListingItemNested} ${classes.lookupListingItem}`}
                style={browseLookUp ? inlineStyles.nestedBrowseListItemBG : inlineStyles.nestedListItem}
                itemSelectedStyle={browseLookUp ? inlineStyles.browseListItemSelected : inlineStyles.listItemSelected}
                idProp={cascadingListKeyField}
                dataKeys={cascadingListFields}
                beginDrag={this.beginDrag}
                endDrag={this.endDrag}
                newEvent={this.newEvent}
                listItemOnClick={(e, idx, id) => { this.handleListItemSelectionChange(e, idx, id); }}
              />
            );
          })}
        </List>
      </Collapse>);
  }

  render(): React.ReactNode {
    const {
      listItems, browseLookUp, loadingPrevPage, loadingNextPage, classes,
      cascadingListFields, cascadingListKey, fieldsToDisplay, keyField, isItemDraggable
    } = this.props;

    const ChildList = this.renderListChildList;

    return (
      <Scrollbars
        className={styles.scrollbarStyle}
        ref={this.scrollBars}
        onScroll={this.handleScroll}
      >
        <List
          component='nav'
          style={{
            padding: 0,
            paddingRight: this.isOverflown() ? '10px' : 0
          }}
          className={styles.lookupListingList}>
          {loadingPrevPage && <ListItem
            button={!cascadingListFields}
            className={styles.lookupListingItem}
            style={inlineStyles.loadingItem}
            disabled>
            <div className={styles.loadingItem}>
              <CircularProgress size={CIRCULAR_PROGRESS_SIZE} style={inlineStyles.preLoader} color={'secondary'} variant={'indeterminate'} />
            </div>
          </ListItem>
          }

          {listItems && listItems.map((item, i) => {
            const isExpandable = cascadingListFields && !isNull(item[cascadingListKey]);

            return (
              <React.Fragment key={`wrapper-${i}`}>
                <DraggableListItem
                  {...this.props}
                  {...this.state}
                  index={i}
                  uniqueKey={i + 1}
                  item={item}
                  isDraggable={isItemDraggable && isItemDraggable(item)}
                  expandable={isExpandable}
                  className={classes.lookupListingItem}
                  style={browseLookUp ? inlineStyles.browseListItemBG : inlineStyles.listItemBG}
                  itemSelectedStyle={browseLookUp ? inlineStyles.browseListItemSelected : inlineStyles.listItemSelected}
                  idProp={keyField}
                  dataKeys={fieldsToDisplay}
                  toggleCollapse={this.toggleCollapse}
                  beginDrag={this.beginDrag}
                  endDrag={this.endDrag}
                  newEvent={this.newEvent}
                  listItemOnClick={(e, idx, id) => {
                    this.handleListItemSelectionChange(e, idx, id);
                  }}
                />
                {cascadingListFields && item[cascadingListKey] &&
                  <ChildList
                    key={`child${i + 1}`}
                    index={i}
                    children={item[cascadingListKey] as any as U[]} />}
              </React.Fragment>

            );
          })}
          {loadingNextPage && <ListItem
            button
            className={styles.lookupListingItem}
            style={inlineStyles.loadingItem}
            disabled>
            <div className={styles.loadingItem}>
              <CircularProgress size={CIRCULAR_PROGRESS_SIZE} style={inlineStyles.preLoader} color={'secondary'} variant={'indeterminate'} />
            </div>
          </ListItem>
          }
        </List>
      </Scrollbars>
    );
  }

}
const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    triggerSchedulerDataChange: activitySchedulerActions.triggerSchedulerDataChange,
  },
  dispatch);

// Does not handle generics - so just go to the any type for now.
export default connect(null, mapDispatchToProps)(withStyles(inlineStyles, { index: 1 })(LookUpList)) as any;
